import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile } from '../types';
import { authService } from '../services/api';
import { ArrowLeft, Edit, User, Briefcase, DollarSign, Clock, Globe, FileText, Mail, Star, CheckCircle, ExternalLink } from 'lucide-react';

const MyFreelancerProfile: React.FC = () => {
  const { user, userProfile } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        if (userProfile) {
          setProfile(userProfile);
        } else {
          // Try to fetch profile from API
          const fetchedProfile = await authService.getProfile();
          setProfile(fetchedProfile);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        // Use basic user info if profile fetch fails
        setProfile({
          email: user?.email || '',
          name: user?.name || '',
          userType: user?.userType || 'freelancer',
          skills: [],
          experience_years: 0,
          cost_per_project: 0,
          bio: '',
          portfolio_url: '',
          availability: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (user) {
      loadProfile();
    } else {
      navigate('/login');
    }
  }, [user, userProfile, navigate]);

  if (!user || user.userType !== 'freelancer') {
    navigate('/');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-white/70">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-white/70 hover:text-white mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back</span>
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-white">My Freelancer Profile</h1>
              <p className="text-white/70 mt-2">
                Your professional profile and portfolio
              </p>
            </div>
            <button
              onClick={() => navigate('/edit-profile')}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
            >
              <Edit size={16} />
              <span>Edit Profile</span>
            </button>
          </div>
        </div>

        {/* Profile Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Profile Card */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                <User size={20} />
                <span>Personal Information</span>
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Full Name
                  </label>
                  <p className="text-lg text-white">{profile?.name || 'Not specified'}</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Email
                  </label>
                  <div className="flex items-center space-x-2">
                    <Mail size={16} className="text-white/40" />
                    <p className="text-white">{profile?.email || 'Not specified'}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1">
                    Account Type
                  </label>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                    <Briefcase size={14} className="mr-1" />
                    Freelancer
                  </span>
                </div>
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                <Briefcase size={20} />
                <span>Professional Information</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1 flex items-center space-x-1">
                    <Clock size={16} />
                    <span>Years of Experience</span>
                  </label>
                  <p className="text-2xl font-bold text-white">
                    {profile?.experience_years || 0}
                  </p>
                  <p className="text-sm text-white/50">years</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-white/60 mb-1 flex items-center space-x-1">
                    <DollarSign size={16} />
                    <span>Cost per Project</span>
                  </label>
                  <p className="text-2xl font-bold text-white">
                    ${profile?.cost_per_project || 0}
                  </p>
                  <p className="text-sm text-white/50">per project</p>
                </div>
              </div>
            </div>

            {/* Skills */}
            {profile?.skills && profile.skills.length > 0 && (
              <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                  <Star size={20} />
                  <span>Skills & Expertise</span>
                </h3>
                
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-500/20 text-primary-300 border border-primary-500/30"
                    >
                      <CheckCircle size={14} className="mr-1" />
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Bio */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                <FileText size={20} />
                <span>About Me</span>
              </h3>
              
              {profile?.bio ? (
                <p className="text-white/80 leading-relaxed">{profile.bio}</p>
              ) : (
                <div className="text-center py-8">
                  <FileText size={48} className="text-white/30 mx-auto mb-4" />
                  <p className="text-white/60 mb-2">No bio yet</p>
                  <p className="text-sm text-white/50">
                    Add a bio to help clients understand your expertise and experience
                  </p>
                </div>
              )}
            </div>

            {/* Portfolio */}
            {profile?.portfolio_url && (
              <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
                <h3 className="text-lg font-medium text-white flex items-center space-x-2 mb-4">
                  <Globe size={20} />
                  <span>Portfolio</span>
                </h3>
                
                <a
                  href={profile.portfolio_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 text-primary-300 hover:text-primary-200 font-medium"
                >
                  <span>View Portfolio</span>
                  <ExternalLink size={16} />
                </a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/projects')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md font-medium"
                >
                  <Briefcase size={16} />
                  <span>Find Projects</span>
                </button>
                
                <button
                  onClick={() => navigate('/edit-profile')}
                  className="w-full flex items-center justify-center space-x-2 px-4 py-2 border border-white/20 text-white/70 hover:bg-white/10 rounded-md font-medium"
                >
                  <Edit size={16} />
                  <span>Edit Profile</span>
                </button>
              </div>
            </div>

            {/* Profile Stats */}
            <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-medium text-white mb-4">Profile Status</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60">Profile Complete</span>
                  <span className={`text-sm font-medium ${
                    profile?.bio && profile?.skills && profile.skills.length > 0 ? 'text-green-300' : 'text-yellow-300'
                  }`}>
                    {profile?.bio && profile?.skills && profile.skills.length > 0 ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60">Availability</span>
                  <span className={`text-sm font-medium ${
                    profile?.availability ? 'text-green-300' : 'text-red-300'
                  }`}>
                    {profile?.availability ? 'Available' : 'Unavailable'}
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-white/60">Skills Listed</span>
                  <span className="text-sm text-white/50">
                    {profile?.skills?.length || 0} skills
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyFreelancerProfile;
